import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDashboard } from './useDashboard';
import { api } from '@/lib/api';

export interface TierInfo {
  current: string;
  label: string;
  totalSpendBrl: number;
  nextTier: string | null;
  nextTierSpendBrl: number | null;
  spendRemaining: number | null;
  isOverride: boolean;
  features: {
    allowedSurfaces: string[];
    marketIntelligence: false | 'basic' | 'full';
    opportunities: boolean;
    pdfExport: boolean;
  };
}

export interface TierUpgradeRequest {
  id: string;
  requestedTier: string;
  status: string;
  reviewNote: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export function useTier() {
  const { data, isLoading, isError, refetch } = useDashboard();
  const tierInfo = data?.tierInfo ?? null;

  /**
   * `canAccess` devolve `false` tanto para "seu plano nao inclui" quanto para
   * "a busca falhou e eu nao sei qual e o seu plano" — e falhar fechado e o
   * lado certo de errar numa checagem de direito de acesso. O problema e a
   * TELA nao distinguir os dois: sem isto, um parceiro Ouro cujo dashboard
   * falhou via cadeado no que pagou, com um convite para fazer upgrade para o
   * tier que ja tem. `tierKnown` deixa a tela dizer a verdade sem afrouxar o
   * guard.
   */
  const tierKnown = tierInfo !== null;
  const tierUnknown = isError && !tierInfo;
  // Enquanto a busca do dashboard esta em voo tambem nao se sabe o tier — e
  // desenhar o cadeado ali afirma ao parceiro Ouro, por uma fracao de segundo
  // a cada carregamento, que ele nao tem o que pagou.
  const tierLoading = isLoading && !tierInfo;

  function canAccess(feature: 'marketIntelligence' | 'opportunities' | 'pdfExport'): boolean {
    if (!tierInfo) return false;
    if (feature === 'marketIntelligence') return tierInfo.features.marketIntelligence !== false;
    return tierInfo.features[feature];
  }

  function isSurfaceAllowed(surface: string): boolean {
    if (!tierInfo) return false;
    return tierInfo.features.allowedSurfaces.includes(surface);
  }

  return { tierInfo, canAccess, isSurfaceAllowed, tierKnown, tierUnknown, tierLoading, refetchTier: refetch };
}

export function useTierUpgradeRequest() {
  return useQuery<{ request: TierUpgradeRequest | null }>({
    queryKey: ['tier-upgrade-request'],
    queryFn: async () => {
      const res = await api.get('/partners/my/tier-upgrade-request');
      return res.data;
    },
  });
}

export function useRequestTierUpgrade() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { requestedTier: string; reason: string }) => {
      const res = await api.post('/partners/my/tier-upgrade-request', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-upgrade-request'] });
    },
  });
}
